import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, GlobeAltIcon, CogIcon, HeartIcon } from '@heroicons/react/24/outline';


import AboutImg from "../Images/About/About.jpg";

const About = () => {
  const values = [
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: 'Premium Quality',
      description: 'Modern manufacturing, meticulous finishing, and rigorous quality checks at every step.'
    },
    {
      icon: <GlobeAltIcon className="h-8 w-8" />,
      title: 'Global Reach',
      description: 'A dependable export network ensuring smooth coordination, documentation, and on-time delivery.'
    },
    {
      icon: <HeartIcon className="h-8 w-8" />,
      title: 'Sustainability & Ethics',
      description: 'Environmentally conscious processes, ethical labor practices, and a long-term view of responsible growth.'
    },
    {
      icon: <CheckCircleIcon className="h-8 w-8" />,
      title: 'Dependability',
      description: 'Consistent product standards and transparent communication every season, every order.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    //<section id="about" className="py-20 lg:py-32 bg-gradient-to-br from-honeycomb-grey-50 to-white">
    <section id="about" className="py-12 lg:py-12 bg-gradient-to-br from-honeycomb-grey-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          {/* Left Column - Image */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
                src={AboutImg} 
                alt="Honeycomb Exports Manufacturing Team"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-honeycomb-blue-900/20 to-transparent" />
            </div>
            
            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-2xl border border-honeycomb-grey-100"
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-honeycomb-blue-600 mb-1">{new Date().getFullYear() - 2001}+</div>
                <div className="text-sm text-honeycomb-grey-600 font-medium">Years of Excellence</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            variants={itemVariants}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-honeycomb-blue-100 text-honeycomb-blue-800 rounded-full text-sm font-medium"
              >
                About Honeycomb Exports
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-4xl lg:text-5xl font-display font-bold text-honeycomb-grey-900 leading-tight"
              >
                Global fashion,
                <span className="text-honeycomb-blue-600"> crafted with precision since 2001</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-lg text-honeycomb-grey-600 leading-relaxed"
              >
                Founded in 2001 by Bheesham and Anita Chopra, Honeycomb Exports has grown into a trusted garment manufacturer known for dependable quality and personalised service. From our base in India, we create casual apparel for men, women, and children, supplying fashion brands and retailers in 25 + countries.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="text-lg text-honeycomb-grey-600 leading-relaxed"
              >
                What began as a small, hands-on operation built on craftsmanship and ethics has evolved into a modern export house, one that still values relationships as much as results. We believe every successful partnership is rooted in clarity, trust, and consistency.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="text-lg text-honeycomb-grey-600 leading-relaxed"
              >
                Our young, agile team brings together more than 100 man-years of experience across sourcing, production, and merchandising. We work closely with every buyer, understanding specifications, timelines, and market needs to deliver knitted and woven garments that reflect shared commitment and care.
              </motion.p>
            </div>

            {/* Values Grid */}
            {/*<motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            >
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-honeycomb-grey-100 hover-lift"
                >
                  <div className="text-honeycomb-blue-600 mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-honeycomb-grey-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-honeycomb-grey-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>*/}
        </motion.div>
        </motion.div>
   {/* Centered Founders Note Section */}
    <motion.blockquote
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.7 }}
      className="max-w-3xl mx-auto mt-20 px-6 py-8 bg-honeycomb-blue-50 rounded-xl shadow-lg text-center text-lg font-medium text-honeycomb-grey-800"
    >
      <h2 className="text-3xl font-display font-bold text-honeycomb-blue-700 mb-8 text-center">
        Founders’ Note
      </h2>
      “At Honeycomb Exports, we’ve always believed that fashion manufacturing is as much about people as it is about product.
      Our team, our buyers, and our shared pursuit of quality form the true fabric of what we do.”
      <footer className="mt-6 text-honeycomb-blue-600 font-semibold">
        — Bheesham & Anita Chopra
      </footer>
    </motion.blockquote>
          {/* CTA Button */}
          {/*  <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="flex justify-center pt-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-honeycomb-blue-600 text-white px-8 py-4 rounded-full  text-center font-semibold hover:bg-honeycomb-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl btn-primary"
                onClick={() => {
                  document.querySelector('#contact')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }}
              >
                Partner With Us
              </motion.button>
            </motion.div>*/}  
      </div>
    </section>
  );
};

export default About;
